import React from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import { Checkbox } from "@picklebet/ui";
import { RadioGroup, RadioGroupItem } from "@picklebet/ui";
import { triggerHaptic } from "@/utilities/haptics";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import {
  usePickMultiLegForEvent,
  useToggleEventDeactivation,
} from "@/features/betslip/hooks/useBetslipActions";
import type { BetSelection } from "@/features/betslip/types";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { InlineAlert } from "@/features/betslip/components/parts/InlineAlert";
import { SelectionEventStatus } from "@/features/betslip/components/parts/SelectionEventStatus";
import { MultiLegOption } from "@/features/betslip/components/multi/MultiLegOption";

import * as styles from "@/features/betslip/components/multi/EventGroup.module.scss";

type EventGroupProps = {
  eventId: string;
  bets: BetSelection[];
};

export const EventGroup = ({ eventId, bets }: EventGroupProps) => {
  const { t } = useTranslation();
  const isDeactivated = useBetslipStore((s) => s.excludedEventIds.has(eventId));
  const multiActiveLegs = useBetslipStore((s) => s.multiActiveLegs);
  const isStakeLocked = useBetslipStore((s) => s.isStakeLocked);
  const isBlocked = useBetslipStore((s) => s.multiBlockedEventIds.has(eventId));
  const toggleEventDeactivation = useToggleEventDeactivation();
  const pickMultiLegForEvent = usePickMultiLegForEvent();

  const headBet = bets[0];
  const title = useTitle((headBet?.sport ?? "").toLowerCase());

  if (!headBet) return null;

  // Only offer a leg choice when the event has more than one selection — a
  // radio on a sole option is meaningless.
  const showRadio = bets.length > 1;
  const activeOutcomeId =
    bets.find((bet) => multiActiveLegs.includes(bet.outcomeId))?.outcomeId ??
    "";
  const hasActiveLeg = activeOutcomeId !== "";
  // Selected = a leg of this event is live in the multi (expanded). Otherwise
  // the group is de-selected and collapses.
  const isSelected = hasActiveLeg && !isDeactivated;

  const handleToggleEvent = () => {
    triggerHaptic.click();
    if (hasActiveLeg) {
      // A leg is in the multi — toggle the event in/out; the pick is preserved.
      toggleEventDeactivation(eventId);
    } else {
      // Not in the multi yet — activate by picking the first leg. (No-op while
      // blocked, but the toggle is disabled in that case anyway.)
      pickMultiLegForEvent(headBet.outcomeId);
    }
  };

  const handlePick = (outcomeId: string) => {
    triggerHaptic.click();
    pickMultiLegForEvent(outcomeId);
  };

  return (
    <div className={styles.eventGroup} data-testid={`event-group-${eventId}`}>
      <BetCardHeader
        icon={<TitleIcon title={title} />}
        title={headBet.eventName}
        clamp={<SelectionEventStatus selection={headBet} />}
        isDeactivated={!isSelected}
        testId={`header-multi-${eventId}`}
        rightSlot={
          <Checkbox
            size="small"
            noSpacing
            checked={isSelected}
            onCheckedChange={handleToggleEvent}
            disabled={isStakeLocked || isBlocked}
            data-testid={`event-toggle-${eventId}`}
          />
        }
      />

      {/* A blocked leg keeps its row on screen dimmed; this explains why, at
          full opacity so it reads clearly. */}
      {isBlocked && (
        <InlineAlert
          alert={{
            variant: "warning",
            level: "outcome",
            message: `${t("Multi")} not available for this selection`,
          }}
        />
      )}

      {/* Groups keep their leg rows on screen (dimmed and inert) rather than
          collapsing: a deactivated group shows the pick it'll re-add, and a
          blocked group — its legs conflict with a live leg elsewhere — shows
          the leg that can't join, under the alert above. */}
      {(hasActiveLeg || isBlocked) && (
        <RadioGroup
          className={cx(styles.legGroup, {
            [styles.legGroupDeactivated]: !isSelected,
          })}
          value={activeOutcomeId}
          onValueChange={handlePick}
        >
          {bets.map((bet) => {
            const radio = showRadio ? (
              <RadioGroupItem
                size="small"
                value={bet.outcomeId}
                disabled={isStakeLocked || !isSelected}
                aria-label={`Use ${bet.eventName} for the ${t("Multi")}`}
              />
            ) : null;

            return (
              <MultiLegOption
                key={bet.outcomeId}
                bet={bet}
                radio={radio}
                isPicked={showRadio && bet.outcomeId === activeOutcomeId}
                isSelected={isSelected}
                showAlert={!isBlocked}
              />
            );
          })}
        </RadioGroup>
      )}
    </div>
  );
};
